import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemDto } from '../items/types';
import { Barcode } from './Barcode';

export interface LabelEntry {
  item: ItemDto;
  /** Pre-resolved barcode value the operator chose for the label.
   *  Items with multiple barcodes default to the primary; the picker
   *  also lets the operator print the SKU as a CODE128 fallback for
   *  items with no barcodes registered. */
  value: string;
  /** Format passed to JsBarcode — operator picks per label. */
  format: 'CODE128' | 'EAN13';
}

interface Props {
  labels: LabelEntry[];
  /** Currency code for the price tag. Defaults to ILS (pilot tenant). */
  currency?: string;
}

const COLS = 3;

// A4 portrait, 3 × 10 = 30 labels per sheet at the Avery 5160 grid.
// Pure CSS layout; the actual print is the browser's native dialog
// (operator hits Cmd/Ctrl+P, picks the printer + paper). `@media print`
// rules in globals.css strip the rest of the page chrome.

export const LabelSheet = forwardRef<HTMLDivElement, Props>(function LabelSheet(
  { labels, currency = 'ILS' },
  ref,
) {
  const { i18n } = useTranslation();
  const priceFmt = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });

  if (labels.length === 0) {
    return null;
  }

  return (
    <div ref={ref} className="label-sheet-print bg-white">
      <div
        className="grid gap-2 p-3"
        style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }}
      >
        {labels.map((entry, idx) => {
          const name =
            i18n.language === 'ar'
              ? entry.item.nameAr
              : (entry.item.nameEn ?? entry.item.nameAr);
          return (
            <div
              key={`${entry.item.itemId}-${idx}`}
              className="label-cell border border-[var(--wheels-border)] rounded-sm p-2 flex flex-col items-center justify-between text-center"
              style={{ minHeight: '90px' }}
            >
              <div className="text-xs font-medium leading-tight line-clamp-2 w-full">
                {name}
              </div>
              <Barcode value={entry.value} format={entry.format} height={36} barWidth={1.2} />
              <div className="text-sm font-bold font-numeric">
                {priceFmt.format(entry.item.basePrice)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
